
import { formatCurrency as formatCurrencyAmount } from './locale';

export type CurrencyCode = 'USD' | 'EUR' | 'MAD';

export const SUPPORTED_CURRENCIES: CurrencyCode[] = ['USD', 'EUR', 'MAD'];

export const CONVERSION_MAP: Record<CurrencyCode, number> = {
  USD: 1,
  EUR: 0.92,
  MAD: 10,
};

export const normalizeCurrency = (value?: string): CurrencyCode => {
  if (value && SUPPORTED_CURRENCIES.includes(value as CurrencyCode)) {
    return value as CurrencyCode;
  }
  return 'USD';
};

/**
 * Format a price for display.
 * @param amount - Base amount in USD
 * @param currency - Target currency
 * @param eurAmount - Optional specific EUR amount (for rounded display)
 */
export const formatPrice = (amount: number, currency: CurrencyCode, eurAmount?: number): string => {
  let displayAmount: number;

  if (currency === 'EUR' && eurAmount !== undefined) {
    displayAmount = eurAmount;
  } else {
    displayAmount = amount * CONVERSION_MAP[currency];
  }

  return formatCurrencyAmount(displayAmount, currency);
};

/** Build a per-currency PAYG unit price formatter (mirrors formatResumePrice). */
const buildUnitPriceFormatter =
  (prices: Record<CurrencyCode, number>) =>
  (currency: CurrencyCode): string => {
    const amount = prices[currency];
    const fractionDigits = currency === 'MAD' ? 0 : 2;
    return formatCurrencyAmount(amount, currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

/**
 * Per-resume screening price by currency (display-only).
 * Hand-tuned per currency so the rounded figure always reads cleanly
 * (USD $0.50, EUR €0.40, MAD 5) rather than a raw FX conversion.
 */
export const RESUME_PRICE: Record<CurrencyCode, number> = {
  USD: 0.5,
  EUR: 0.4,
  MAD: 5,
};

/** Format the per-resume price with sub-unit precision where it matters. */
export const formatResumePrice = buildUnitPriceFormatter(RESUME_PRICE);

/**
 * Per-search PAYG price (one "talent cloud search").
 * Hand-tuned starting point, same styling convention as RESUME_PRICE —
 * confirm with billing/product before treating as final.
 */
export const TALENT_SEARCH_PRICE: Record<CurrencyCode, number> = {
  USD: 2,
  EUR: 1.8,
  MAD: 20,
};

export const formatTalentSearchPrice = buildUnitPriceFormatter(TALENT_SEARCH_PRICE);

/**
 * Per-unlock PAYG price (one "talent unlock", reveals full contact info).
 * Hand-tuned starting point, same styling convention as RESUME_PRICE —
 * confirm with billing/product before treating as final.
 */
export const TALENT_UNLOCK_PRICE: Record<CurrencyCode, number> = {
  USD: 1,
  EUR: 0.9,
  MAD: 10,
};

export const formatTalentUnlockPrice = buildUnitPriceFormatter(TALENT_UNLOCK_PRICE);
